// generated-app-data-model.ts — Sprint 10: the versioned generated-app data model.
//
// Booking shipped records with NO schema version and an ad-hoc shape, so a future spec edit
// (collection rename, field change) could orphan or mis-read persisted rows (GAP-SAFEEDIT).
// This module defines, ONCE, the versioned envelope every generated-app record carries and the
// per-app data-model descriptor, so edits can be checked for compatibility instead of silently
// breaking data.
//
// Pure, framework-free. It composes the existing pieces (collection-contracts.ts + the live
// app_data row = the `data` jsonb) — it does NOT replace data-plane.ts (the in-memory reference
// model) and does NOT change the live row columns. The envelope fields live inside the `data`
// jsonb (so no DB migration is required to start versioning records).

import {
  getCollectionContract,
  validateRecord,
  type RecordVisibility,
  type RecordValidationResult,
} from './collection-contracts';
import { getLifecycle, type StatusEvent } from './status-lifecycle';

/** Platform-wide record-envelope version. Bump when the envelope shape changes; records
 *  carry their `schemaVersion` so a reader can detect + migrate older rows. */
export const GENERATED_APP_SCHEMA_VERSION = 1;

/** Per-category schema version. Bump a category's number when its collection set / field
 *  contracts change in a way a reader must account for. All start at 1. */
export const CATEGORY_SCHEMA_VERSIONS: Record<string, number> = {
  booking: 1,
};

export function categorySchemaVersion(category: string): number {
  return CATEGORY_SCHEMA_VERSIONS[category] ?? 1;
}

/** The per-app data-model descriptor — what a generated app's data layer looks like.
 *  Persist this alongside the app (e.g. D1 app_metadata) so edits can be versioned. */
export interface AppDataModel {
  appId: string;
  appCategory: string;
  /** the platform envelope version this app was generated against. */
  appSchemaVersion: number;
  /** the category contract version this app was generated against. */
  categorySchemaVersion: number;
  /** collection names this app uses (must be known collection contracts to be platform-managed). */
  collections: string[];
}

export interface RecordAudit {
  createdBy?: string;
  updatedBy?: string;
  statusHistory?: StatusEvent[];
}

/** The metadata envelope carried inside each record's `data` jsonb (additive; no DB change). */
export interface VersionedRecordMeta {
  schemaVersion: number;
  visibility: RecordVisibility;
  status?: string;
  audit?: RecordAudit;
}

export interface BuildAppDataModelInput {
  appId: string;
  category: string;
  collections: string[];
}

export function buildAppDataModel(input: BuildAppDataModelInput): AppDataModel {
  return {
    appId: input.appId,
    appCategory: input.category,
    appSchemaVersion: GENERATED_APP_SCHEMA_VERSION,
    categorySchemaVersion: categorySchemaVersion(input.category),
    collections: [...input.collections],
  };
}

/** A record's schemaVersion is compatible if it is <= the current envelope version (older rows
 *  are migrate-forward; a future-versioned row from newer code is NOT readable by older code). */
export function isCompatibleSchema(
  recordVersion: number,
  currentVersion: number = GENERATED_APP_SCHEMA_VERSION
): boolean {
  return Number.isInteger(recordVersion) && recordVersion >= 1 && recordVersion <= currentVersion;
}

export interface AppDataModelValidation {
  ok: boolean;
  errors: string[];
}

/** Validate an app data-model descriptor: known schema versions + every collection is a known,
 *  platform-managed contract (so the owner route + validators understand it). */
export function validateAppDataModel(model: AppDataModel): AppDataModelValidation {
  const errors: string[] = [];
  if (!isCompatibleSchema(model.appSchemaVersion)) errors.push(`bad_app_schema_version:${model.appSchemaVersion}`);
  if (!Number.isInteger(model.categorySchemaVersion) || model.categorySchemaVersion < 1) {
    errors.push(`bad_category_schema_version:${model.categorySchemaVersion}`);
  }
  if (!Array.isArray(model.collections) || model.collections.length === 0) {
    errors.push('no_collections');
  }
  for (const c of model.collections || []) {
    if (!getCollectionContract(c)) errors.push(`unknown_collection:${c}`);
  }
  return { ok: errors.length === 0, errors };
}

export interface RecordEnvelopeValidation extends RecordValidationResult {
  visibility?: RecordVisibility;
}

/** Validate a single record for a collection: schema-version compatibility (when the envelope
 *  meta is present) + the collection-contract shape (required fields, types, status enum). */
export function validateAppRecord(
  collection: string,
  payload: unknown,
  meta?: Partial<VersionedRecordMeta>
): RecordEnvelopeValidation {
  const contract = getCollectionContract(collection);
  if (!contract) return { ok: false, errors: [`unknown_collection:${collection}`] };
  const base = validateRecord(collection, payload);
  const errors = [...base.errors];
  if (meta && meta.schemaVersion !== undefined && !isCompatibleSchema(meta.schemaVersion)) {
    errors.push(`incompatible_schema_version:${meta.schemaVersion}`);
  }
  return { ok: errors.length === 0, errors, visibility: contract.visibility };
}

/** Stamp the versioned envelope onto a record payload (additive — leaves app fields intact). */
export function withEnvelope(
  collection: string,
  payload: Record<string, unknown>,
  opts: { actor?: string; now: number } = { now: 0 }
): Record<string, unknown> {
  const contract = getCollectionContract(collection);
  const meta: VersionedRecordMeta = {
    schemaVersion: GENERATED_APP_SCHEMA_VERSION,
    visibility: contract?.visibility ?? 'customer-private',
  };
  if (contract?.statusField && payload[contract.statusField] !== undefined) {
    meta.status = String(payload[contract.statusField]);
  }
  if (opts.actor) meta.audit = { createdBy: opts.actor, updatedBy: opts.actor };
  return { ...payload, __meta: meta };
}

/** Does this collection have a status lifecycle (so the owner route can validate transitions)? */
export function collectionLifecycleId(collection: string): string | null {
  return getCollectionContract(collection)?.lifecycle ?? null;
}

export function describeAppDataModel(model: AppDataModel): string {
  const lines = [
    `app=${model.appId} category=${model.appCategory}`,
    `appSchemaVersion=${model.appSchemaVersion} categorySchemaVersion=${model.categorySchemaVersion}`,
  ];
  for (const c of model.collections) {
    const ct = getCollectionContract(c);
    const life = collectionLifecycleId(c);
    lines.push(
      `  - ${c}: owner=${ct?.owner} visibility=${ct?.visibility}` +
        (life ? ` lifecycle=${life}(${getLifecycle(life)?.states.length} states)` : '') +
        (ct?.uniqueBy ? ` uniqueBy=[${ct.uniqueBy.join(',')}]` : '')
    );
  }
  return lines.join('\n');
}
